import React, {
	PropsWithChildren,
	useCallback,
	useEffect,
	useState,
} from "react";

import { useQueryClient } from "@tanstack/react-query";

import AuthService from "@/services/AuthService";
import UserService from "@/services/UserService";
import {
	isRefreshRefused,
	onTokenChange,
	setHeaderToken,
} from "@/services/api";
import {
	clearSessionHint,
	hasSessionHint,
	setSessionHint,
} from "@/services/sessionHint";

import { clearUserStorage } from "@/utils/UUserStorage";

import { meKeys } from "@/constants/CQueryKeys";

import { TToken, TUser } from "@/types";

import { AuthContext } from "./context";

/**
 * Carries a logout across tabs.
 *
 * The old implementation watched the `storage` event, which only fires when a
 * localStorage key changes. Nothing is written to storage any more, so the
 * tabs talk to each other directly.
 */
const AUTH_CHANNEL = "chronos:auth";

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const [token, setToken] = useState<TToken | null>(null);
	const [user, setUser] = useState<TUser | null>(null);
	// Nothing can be rendered before the refresh cookie has been redeemed:
	// isAuthenticated is false until then, and PrivateRoute would bounce an
	// authenticated user to /login on every page load.
	const [isBootstrapping, setIsBootstrapping] = useState(true);
	const queryClient = useQueryClient();

	const isAuthenticated = !!token?.access_token;

	const login = (newToken: TToken, newUser: TUser) => {
		setHeaderToken(newToken.access_token);
		setSessionHint();
		setToken(newToken);
		setUser(newUser);
	};

	const clearSession = useCallback(() => {
		setHeaderToken(null);
		clearSessionHint();
		clearUserStorage();
		setToken(null);
		setUser(null);
		queryClient.clear();
	}, [queryClient]);

	const logout = useCallback(() => {
		AuthService.logout().finally(() => {
			clearSession();
			new BroadcastChannel(AUTH_CHANNEL).postMessage("logout");
			window.location.assign("/login");
		});
	}, [clearSession]);

	const updateUser = (newUser: TUser) => setUser(newUser);

	const hasRole = (roles: string[], all = false) => {
		if (!user?.roles) return false;
		if (all) return roles.every((r) => user.roles?.includes(r));
		return roles.some((r) => user.roles?.includes(r));
	};

	// Redeem the HttpOnly refresh cookie for an access token on every load.
	// This is what replaces reading a token out of storage: the session lives
	// in a cookie the page cannot read, so the only way to know whether one
	// exists is to ask the API.
	useEffect(() => {
		// No record of a login in this browser: there is no cookie to redeem,
		// so skip the call that would only 400. A stale hint is harmless - the
		// call below fails and clears it.
		if (!hasSessionHint()) {
			setIsBootstrapping(false);
			return;
		}

		let cancelled = false;

		AuthService.refreshToken()
			.then(async (data: TToken) => {
				if (cancelled || !data?.access_token) return;

				setHeaderToken(data.access_token);
				const me = await queryClient.fetchQuery({
					queryKey: meKeys.all,
					queryFn: () => UserService.fetchMe(data.access_token),
					staleTime: 30_000,
				});

				if (cancelled) return;
				setToken(data);
				setUser(me);
			})
			.catch((error) => {
				// No cookie, or it was rejected: simply not signed in. Drop the
				// hint so the next load does not ask again. Not on a network
				// failure: the session may well be fine, and dropping the hint
				// would stop the next load from even trying.
				if (isRefreshRefused(error)) clearSessionHint();
			})
			.finally(() => {
				if (!cancelled) setIsBootstrapping(false);
			});

		return () => {
			cancelled = true;
		};
		// Bootstrap runs once, on mount: `login`/`logout` read here are
		// stable setters, and re-running on their identity would re-ask
		// the refresh cookie on every render of the provider.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// The refresh interceptor renews the token from inside an axios call, with
	// no component involved, so React has to be told about it.
	useEffect(
		() =>
			onTokenChange((newToken) => {
				setToken((current) =>
					newToken && current
						? { ...current, access_token: newToken }
						: current
				);
			}),
		[]
	);

	useEffect(() => {
		const channel = new BroadcastChannel(AUTH_CHANNEL);
		channel.onmessage = (event) => {
			if (event.data === "logout") clearSession();
		};

		return () => channel.close();
	}, [clearSession]);

	useEffect(() => {
		const handleAuthLogout = () => logout();
		window.addEventListener("auth:logout", handleAuthLogout);

		return () =>
			window.removeEventListener("auth:logout", handleAuthLogout);
	}, [logout]);

	if (isBootstrapping) return null;

	return (
		<AuthContext.Provider
			value={{
				token,
				user,
				isAuthenticated,
				login,
				logout,
				updateUser,
				hasRole,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};
